import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import moment from '@app/utils/moment'

import type { ApiConsentType, ApiPolicy } from '@app/packages/api/v2/types/entities'
import { getRelationshipValue } from '@app/packages/apiTools/getRelationshipValue'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { routingDateSelector } from './routing'

type Policies = { policies: ApiPolicy[]; consentTypes: ApiConsentType[] }

const policiesSlice = createReduxSlice<SliceState<Policies>>('policies')

export const policiesSelector = createSelector([policiesSlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: policiesSlice,
      date: { date, expiresInMinutes: 24 * 60 },
      load: async dispatch => {
        const { getPolicies } = await import('@app/packages/api/v2/actions/policies')
        const response = await dispatch(callV2Api(getPolicies({ 'filter[effective_at_gt]': date.format() })))
        return {
          policies: response.data.filter(p => moment(p.attributes.effective_from).isSameOrBefore(date)),
          consentTypes: response.included?.filter(t => t.type === 'consent_types') ?? [],
        }
      },
    })
  )
)

export const structuredPoliciesSelector = mapThunkSelector(policiesSelector, data => {
  const policies = data.policies.map(p => ({
    policy: p,
    consentTypes: getRelationshipValue(p.relationships.consent_types).map(
      ct => data.consentTypes.find(c => c.id === ct.id) ?? invariant(`Consent type ${ct.id} not found`)
    ),
  }))
  return policies
})
